import React from 'react';
import { AlertTriangle } from 'lucide-react';

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  const btnBg =
    variant === 'danger'
      ? 'bg-[#D94A5C] hover:bg-[#c0384a] text-white'
      : variant === 'warning'
      ? 'bg-[#EBC138] hover:bg-[#d6ad2b] text-[#111111]'
      : 'bg-[#20A66A] hover:bg-[#198a57] text-white';

  const iconBg =
    variant === 'danger'
      ? 'bg-[#FDEDEF] text-[#D94A5C]'
      : variant === 'warning'
      ? 'bg-[#FFF7D9] text-[#8a6508]'
      : 'bg-[#EAF8F1] text-[#20A66A]';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-fadeIn">
      <div className="bg-white rounded-3xl border border-[#E5E9E9] p-6 max-w-md w-full shadow-2xl space-y-5 animate-scaleUp">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-2xl shrink-0 ${iconBg}`}>
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div className="space-y-1">
            <h3 className="text-lg font-bold text-[#111111]">{title}</h3>
            <p className="text-sm text-[#5F6368] leading-relaxed">{message}</p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-[#E5E9E9]">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2.5 rounded-xl border border-[#E5E9E9] bg-white text-[#5F6368] hover:text-[#111111] hover:bg-[#F0F4F4] text-xs font-bold transition-all cursor-pointer"
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`px-4 py-2.5 rounded-xl text-xs font-bold shadow-xs transition-all cursor-pointer ${btnBg}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};
